import { memo, useCallback, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import {
  MenuItem,
  FormControl,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  FormLabel,
  FormGroup,
  FormHelperText,
  Button,
  InputAdornment,
  IconButton,
  Typography,
  Box,
  Stack,
} from '@mui/material';
import TextField from '../../../../bmr/components/styled/FormField'
import { Visibility, VisibilityOff } from '@mui/icons-material';
import Switch from '../../../../bmr/components/styled/Switch';
import Select from '../../../../bmr/components/styled/SelectField';
import { DatePicker, LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { DeleteIcon } from '../../../../assets/common-svg/CommonSvg';
dayjs.extend(customParseFormat);
dayjs.extend(localizedFormat);

//@info using the placeholder text component to avoid code repetition just in file.
const PlaceholderText = ({ placeholder }) => (
  <span style={{ color: '#9e9e9e' }}>{placeholder}</span>
);
const allowedFormats = ["DD:MM:YY", "DD/MM/YY", "DD-MM-YY"];
const allowedDateFormats = ["DD:MM:YY", "DD/MM/YY", "DD-MM-YY"];
const allowedTimeFormats = ["hh:mm A", "HH:mm"];
const allowedFormatsHours = ["hh:mm A", "HH:mm"];

const FormField = memo(({
  name,
  type = 'text',
  label = '',
  placeholder,
  options = [],
  fullWidth = true,
  rows = 4,
  disabled = false,
  row = '', //@info this row is using for the horziontal radio input.
  description = '',
  inputLabelTop = '',
  rules = {},
  ...props
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const handleTogglePassword = useCallback(() => {
    setShowPassword(prev => !prev);
  }, []);
   const inputRef = useRef(null);
  const renderField = useCallback((field, fieldState) => {
    const { error, isTouched, isDirty } = fieldState;
    const hasError = !!error && (isTouched || isDirty);

    switch (type) {
      case 'label':
        return (
          <Typography
            variant="body1"
            sx={{ fontWeight: 700, color: "text.secondary" }}
          >
            {field.label}
          </Typography>
        )
      case 'text':
      case 'email':
        return (
            <TextField             
              {...field}
              type={type}
              placeholder={placeholder}
              fullWidth={fullWidth}
              error={hasError}
              helperText={error?.message}
              disabled={disabled}
              defaultValue={field?.value || field?.defaultValue || ''}
            InputProps={{
              classes: {
                input: props.className,
              },
            }}
              {...props}
                  sx={{
                ...error && {
                  '& .MuiInputBase-input': {
                        border: '1px solid #ff2023'
                  }
                }
              }}
            />
        );

      case 'number':
        return (
          <TextField
            {...field}
            type="number"
            label={label}
            placeholder={placeholder}
            fullWidth={fullWidth}
            error={hasError}
            helperText={error?.message}
            disabled={disabled}
            onChange={(e) => {
              const value = e.target.value === '' ? '' : Number(e.target.value);
              field.onChange(value);
            }}
            {...props}
          />
        );

      case 'password':
        return (
          <TextField
            {...field}
            type={showPassword ? 'text' : 'password'}
            label={label}
            placeholder={placeholder}
            fullWidth={fullWidth}
            error={hasError}
            helperText={error?.message}
            disabled={disabled}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleTogglePassword}
                    edge="end"
                    aria-label="toggle password visibility"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            {...props}
          />
        );

      case 'textarea':
        return (
            <TextField
              {...field}
              label={label}
              placeholder={placeholder}
              fullWidth={fullWidth}
              multiline
              rows={rows}
              className='mb-3'
              size="small"
              error={hasError}
              helperText={error?.message}
              disabled={disabled}
              {...props}
            />
        );

      // case 'select':
      //   return (
      //     <FormControl fullWidth={fullWidth} error={hasError} disabled={disabled} className='controlledSelect'>
      //       <Select
      //         {...field}
      //         displayEmpty
      //         label={label}
      //         name={label}
      //         value={props?.multiple ? (field?.value || []) : (field?.value ?? '')}
      //         {...props}
      //         onChange={(e) => {
      //         const value = e.target.value;
      //         field.onChange(value);
      //       }}
      //         MenuProps={{
      //           anchorOrigin: {
      //             vertical: 'bottom',
      //             horizontal: 'left',
      //           },
      //           transformOrigin: {
      //             vertical: 'top',
      //             horizontal: 'left',
      //           },
      //           PaperProps: {
      //             style: {
      //               minWidth: '12.5rem',
      //             },
      //           },
      //           disablePortal: true,
      //         }}
      //         renderValue={(selected) => {
      //           if (!selected || selected?.length === 0) {
      //             return <PlaceholderText placeholder={placeholder} />;
      //           }

      //           if (props.multiple) {
      //             return <PlaceholderText placeholder={placeholder} />;
      //           }

      //           const selectedOption = options?.find(opt => opt?.value === selected);
      //           return selectedOption?.label || selected;
      //           // return selected;
      //         }}
      //       >
      //         <MenuItem disabled value="">
      //           <em>{placeholder}</em>
      //         </MenuItem>
      //         {options?.map((option) => (
      //           <MenuItem key={option?.value} value={option?.value} disabled={option?.disabled}>
      //             {option?.label}
      //           </MenuItem>
      //         ))}
      //       </Select>
      //       {hasError && <FormHelperText>{error?.message}</FormHelperText>}
      //     </FormControl>
      //   );



      case 'select':
  return (
    <FormControl
      fullWidth={fullWidth}
      error={hasError}
      disabled={disabled}
      className="controlledSelect"
    >
      <Select
        {...field}
        {...props}
        displayEmpty
        value={
          props?.multiple
            ? field.value || []
            : field.value ?? ''
        }
        onChange={(e) => {
          const value = e.target.value;
         field.onChange(value);
          if (props.onChange) {
            props.onChange(e); 
          }
        }}
        MenuProps={{
          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
          transformOrigin: { vertical: 'top', horizontal: 'left' },
          PaperProps: { style: { minWidth: '12.5rem' } },
          disablePortal: false, //@info to disable scroll up and down
        }}
        renderValue={(selected) => {
          if (!selected || selected?.length === 0) {
            return <PlaceholderText placeholder={placeholder} />;
          }

          if (props.multiple) {
            return <PlaceholderText placeholder={placeholder} />;
          }

          const selectedOption = options?.find(
            (opt) => opt?.value === selected
          );
          return selectedOption?.label || selected;
        }}
      >
        <MenuItem disabled value="">
          <em>{placeholder}</em>
        </MenuItem>

        {options?.map((option) => (
          <MenuItem
            key={option?.value}
            value={option?.value}
            disabled={option?.disabled}
          >
            {option?.label}
          </MenuItem>
        ))}
      </Select>

      {hasError && <FormHelperText>{error?.message}</FormHelperText>}
    </FormControl>
  );  
      case 'switch': {
        const { label1, label2 } = props || {};
        const hasBothLabels = label1 || label2;
        return (
          <FormControl error={hasError} disabled={disabled}>
            {hasBothLabels ? (
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography className="text-sm font-medium">{label1}</Typography>

                <Switch
                  {...props}
                  {...field}
                  checked={!!field.value}
                  onChange={(e, checked) => {
                    field.onChange(checked);   
                    props.onChange?.(checked);
                  }}
                />
                <Typography className="text-sm font-medium">{label2}</Typography>
              </Stack>
            ) : (
              <FormControlLabel
                className="font-medium text-sm ml-0"
                control={
                  <Switch
                  {...props}
                    {...field}
                    checked={!!field.value}
                    onChange={(e, checked) => {
                      field.onChange(checked);
                      props.onChange?.(checked);
                    }}
                  />
                }
                label={label}
              />
            )}

            {hasError && <FormHelperText>{error?.message}</FormHelperText>}
          </FormControl>
        );
      }

      case 'checkbox-group':
        return (
          <FormControl error={hasError} disabled={disabled} component="fieldset">
            <FormLabel component="legend">{label}</FormLabel>
            <FormGroup>
              {options.map((option) => (
                <FormControlLabel
                  key={option.value}
                  control={
                    <Checkbox
                      checked={field.value?.includes(option.value) || false}
                      onChange={(e) => {
                        const currentValue = field.value || [];
                        if (e.target.checked) {
                          field.onChange([...currentValue, option.value]);
                        } else {
                          field.onChange(currentValue.filter(v => v !== option?.value));
                        }
                      }}
                      {...props}
                    />
                  }
                  label={option.label}
                />
              ))}
            </FormGroup>
            {hasError && <FormHelperText>{error?.message}</FormHelperText>}
          </FormControl>
        );

      case 'radio-group':
        return (
           <FormControl
            error={hasError}
            disabled={disabled}
            component="fieldset"
            sx={{ display: "block" }}
          >
            {label && <FormLabel component="legend" className="text-xs">
              {label}
            </FormLabel>
            }
            <RadioGroup
              row={row}
              value={field.value ?? ""}
              onChange={(e) => {
                field.onChange(e.target.value);
                props.onChange?.(e);
              }}
              className='h-8'
            >
              {options.map((option) => (
                <FormControlLabel
                  key={String(option.value)}
                  value={option.value}
                  control={<Radio size="small" color="primary" />}
                  label={option.label}
                />
              ))}
            </RadioGroup>
            {hasError && <FormHelperText>{error?.message}</FormHelperText>}
          </FormControl> 
        );

      case 'file':
      case 'image_upload':
      case 'barcode': {
        const handleFileChange = (e) => {
          const file = e?.target?.files?.[0] || null;
          field.onChange(file);
          e.target.value = '';
        };

        const handleRemoveFile = () => {
          field.onChange(null);

          if (inputRef.current) {
            inputRef.current.value = '';
          }
        };

        return (
          <FormControl fullWidth={fullWidth} error={hasError} disabled={disabled}>
            <input
              ref={inputRef}
              {...props}
              type="file"
              id={name}
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />

            <Box display="flex" gap={1} alignItems="center">
              <label htmlFor={name} style={{ flex: 1 }}>
                <Button
                  variant="outlined"
                  component="span"
                  fullWidth
                  disabled={disabled}
                >
                  {field?.value?.name || label || 'Choose File'}
                </Button>
              </label>

              {field?.value && (
                <IconButton
                  edge="end"
                  aria-label="delete"
                  onClick={handleRemoveFile}
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>

            {hasError && <FormHelperText>{error?.message}</FormHelperText>}
          </FormControl>
        );
      }


      case "date": {
      

        const format =
          props?.format && allowedFormats.includes(props.format)
            ? props.format
            : "DD/MM/YY";

        const parsedValue = field.value
          ? dayjs(field.value, format, true)
          : null;

        return (
          <FormControl fullWidth error={hasError} disabled={disabled}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label={label}
                format={format}
                value={parsedValue}
                onChange={(newValue) => {
                  const formatted = newValue ? newValue.format(format) : "";
                  field.onChange(formatted);

                  // call parent onChange if passed
                  props?.onChange?.(newValue);
                }}
                slotProps={{
                  textField: {
                    size: "small",
                    error: hasError,
                    helperText: hasError ? error?.message : "",
                     ...(placeholder && { placeholder }),
                  },
                }}
                {...props}
              />
            </LocalizationProvider>

            {hasError && <FormHelperText>{error?.message}</FormHelperText>}
          </FormControl>
        );
      }
      case "time": {
        const time_format =
          props?.format && allowedFormatsHours.includes(props.timeFormat)
            ? props.timeFormat
            : "HH:mm A";
        const is24Hour = time_format.includes("H");
        const ampm = !is24Hour;
        const parsedValue = field.value ? dayjs(field.value, time_format, true) : null;

        return (
          <FormControl fullWidth error={hasError} disabled={disabled}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <TimePicker
                label={label}
                value={parsedValue}
                onChange={(newValue) => {
                  field.onChange(newValue ? newValue.format(time_format) : "");
                }}
                ampm={ampm}
                format={time_format}
                slotProps={{
                  textField: {
                    size: "small",
                    error: hasError,
                    helperText: hasError ? error?.message : "",
                  },
                }}
                {...props}
              />
            </LocalizationProvider>

            {hasError && <FormHelperText>{error?.message}</FormHelperText>}
          </FormControl>
        );
      }

      case "datetime": {      

        const dateFormat =
          props?.combineDate && allowedDateFormats.includes(props.combineDate)
            ? props.combineDate
            : "DD/MM/YY";

        const timeFormat =
          props?.combineTime && allowedTimeFormats.includes(props.combineTime)
            ? props.combineTime
            : "hh:mm A";

        const format = `${dateFormat} ${timeFormat}`;

        const is24Hour = timeFormat.includes("H");
        const ampm = !is24Hour;

        const parsedValue = field.value
          ? dayjs(field.value, format, true)
          : null;

        return (
          <FormControl fullWidth error={hasError} disabled={disabled}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                label={label}
                value={parsedValue}
                onChange={(newValue) => {
                  field.onChange(newValue ? newValue.format(format) : "");
                }}
                format={format}
                ampm={ampm}
                views={["year", "month", "day", "hours", "minutes"]}
                openTo="day"
                slotProps={{
                  textField: {
                    size: "small",
                    error: hasError,
                    helperText: hasError ? error?.message : "",
                  },
                }}
              />
            </LocalizationProvider>

            {hasError && <FormHelperText>{error?.message}</FormHelperText>}
          </FormControl>
        );
      }

      case "hyperlink": {
        const linkLabel = props?.linkLabel || "";
        const linkUrl = props?.linkUrl || "";

        return (
          <FormControl fullWidth disabled={disabled}>
            <a
              href={linkUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "#1976d2",
                textDecoration: "underline",
                fontSize: "14px",
                fontWeight: 500,
                cursor: linkUrl ? "pointer" : "not-allowed",
                opacity: linkUrl ? 1 : 0.5
              }}
            >
              {linkLabel}
            </a>

            {hasError && <FormHelperText error>{error?.message}</FormHelperText>}
          </FormControl>
        );
      }

      default:
        return (
          <TextField
            {...field}
            type={type}
            label={label}
            placeholder={placeholder}
            fullWidth={fullWidth}
            error={hasError}
            helperText={error?.message}
            disabled={disabled}
            {...props}
          />
        );
    }
  }, [type, label, placeholder, fullWidth, rows, disabled, options, showPassword, handleTogglePassword, name, props]);
  
  return (
    <Controller
      name={name}
      rules={rules}
      render={({ field, fieldState }) => (
       <Box
  sx={{
    marginBottom: props.noMargin
      ? 0
      : props.size === 'small'
      ? '.5rem'
      : '1rem'
  }}
>

         {inputLabelTop && <Typography variant='p' className='customLabel inline-block'>{inputLabelTop}</Typography>}
          {renderField(field, fieldState)}
          {description && (
            <Box>
            <Typography color={"darkgray"} variant="caption" className='mb-2 text-xs text-[#828282]'  >
              {description}
            </Typography>
            </Box>
          )}
        </Box>
      )}
    />
  );
});

FormField.displayName = 'FormField';

export default FormField;